// Evaluator — takes a `Cabinet` row plus its `ParametricCabinetClass`
// document and emits the same `CabinetPart[]` / `CabinetMeshChunk[]` /
// `DrillPattern` triple the legacy hardcoded classes produced.
//
// Phases:
//   1. Build layered scope:   cabinet dims (W,H,D) → class.params →
//      cabinet.classParams → per-part PartOverride.params.
//   2. For each PartDef: expand `repeat` (loop var in scope), check
//      `enabled`, evaluate `box`/`cut`/`thickness`, apply overrides.
//   3. Emit mesh chunk via boxChunk(min, max, colorFor(materialSlot)).
//   4. Emit cut-list CabinetPart (w/h/thickness/role/grain).
//   5. Emit drilling pattern via drillingTemplates.
//   6. Walk joinery edges and emit auto-screw drillings where a
//      fastener is declared and both endpoints exist.

import type {
  Cabinet,
  CabinetMeshChunk,
  CabinetPart,
  Inches,
  ParametricCabinetClass,
  PartDef,
  PartOverride,
} from "@/lib/types";
import type { Issue } from "../validation";
import type { DrillEntry, DrillPattern } from "..";
import { boxChunk, COLORS } from "../geometry";
import { ExprError, type Scope, asAst, evalAst, evalParams, evalValue, parse } from "./expr";
import { emitDrillings } from "./drillingTemplates";

export interface EvalResult {
  parts: CabinetPart[];
  meshes: CabinetMeshChunk[];
  drillings: DrillPattern;
  /** Issues attached to individual parts (eval-time errors, joinery
   *  geometry mismatches). Caller surfaces in the Problems panel. */
  issues: Issue[];
  /** Evaluated cabinet bounding box. */
  bounds: { w: Inches; h: Inches; d: Inches };
}

const MATERIAL_COLOR: Record<PartDef["materialSlot"], string> = {
  carcass: COLORS.carcass,
  back:    COLORS.back,
  door:    COLORS.door,
  shelf:   COLORS.shelf,
  toe:     COLORS.carcass,
  custom:  COLORS.carcass,
};

// ── Public entry ────────────────────────────────────────────────

export function evaluateClass(
  cabinet: Pick<Cabinet, "width" | "height" | "depth" | "classParams" | "partOverrides">,
  klass: ParametricCabinetClass,
): EvalResult {
  const issues: Issue[] = [];

  const baseScope: Scope = {
    W: cabinet.width,
    H: cabinet.height,
    D: cabinet.depth,
  };

  // Class params — evaluated in declaration order so later params can
  // reference earlier ones. Errors surface as a single class-level issue
  // and abort the rest of evaluation (a broken param can't produce
  // sensible parts).
  let classScope: Scope;
  try {
    classScope = evalParams(klass.params ?? {}, baseScope);
  } catch (e) {
    issues.push(makeIssue(e, undefined, "class params"));
    return { parts: [], meshes: [], drillings: {}, issues,
             bounds: { w: cabinet.width, h: cabinet.height, d: cabinet.depth } };
  }

  // Per-cabinet param overrides — same rules.
  let scope: Scope;
  try {
    scope = evalParams(cabinet.classParams ?? {}, classScope);
  } catch (e) {
    issues.push(makeIssue(e, undefined, "cabinet classParams"));
    scope = classScope;
  }

  // Track which parts ended up enabled and where their boxes ended up
  // (for joinery-driven drilling emission below).
  const emittedBoxes = new Map<string, { min: number[]; max: number[]; partDef: PartDef; cutW: number; cutH: number }>();
  const parts: CabinetPart[] = [];
  const meshes: CabinetMeshChunk[] = [];
  const drillings: DrillPattern = {};

  for (const partDef of klass.parts) {
    const override = cabinet.partOverrides?.[partDef.id] ?? {};
    if (override.enabled === false) continue;

    // Expansions: a part with `repeat` is emitted N times with an
    // index var in scope; non-repeat parts emit once with no index.
    const expansions = expandRepeat(partDef, scope, issues);

    for (const exp of expansions) {
      const partScope: Scope = { ...scope, ...exp.scope };

      // Per-part param layer — evaluated against the class+repeat scope.
      try {
        Object.assign(partScope, evalParams(override.params ?? {}, partScope));
      } catch (e) {
        issues.push(makeIssue(e, partDef.id, "partOverride.params"));
        continue;
      }

      const out = evaluatePart(partDef, override, partScope, exp.id, exp.name, issues);
      if (!out) continue;

      const { cabinetPart, mesh, drillCtx } = out;
      parts.push(cabinetPart);
      meshes.push(mesh);
      drillings[cabinetPart.id] = emitDrillings(drillCtx, partDef.drilling);
      emittedBoxes.set(cabinetPart.id, {
        min: out.min, max: out.max, partDef, cutW: cabinetPart.w, cutH: cabinetPart.h,
      });
    }
  }

  // ── Joinery-driven screw drillings ──
  // For each declared joinery edge with `fastener.kind === "screw"`,
  // add a small screw pattern centred along the mating face. The
  // matesWith id must resolve to an emitted part; otherwise the
  // joinery is silently skipped (the part may have been disabled).
  for (const partDef of klass.parts) {
    if (!partDef.joinery) continue;
    const me = emittedBoxes.get(partDef.id);
    if (!me) continue;
    for (const [face, edge] of Object.entries(partDef.joinery)) {
      if (!edge || !edge.fastener) continue;
      const mate = edge.matesWith ? emittedBoxes.get(edge.matesWith) : undefined;
      if (!mate) continue;
      const screws = makeJointScrews(me, mate, face, scope);
      if (!screws.length) continue;
      const list = drillings[partDef.id] ?? [];
      drillings[partDef.id] = [...list, ...screws];
    }
  }

  return {
    parts, meshes, drillings, issues,
    bounds: { w: cabinet.width, h: cabinet.height, d: cabinet.depth },
  };
}

// ── Internals ───────────────────────────────────────────────────

function expandRepeat(
  partDef: PartDef,
  scope: Scope,
  issues: Issue[],
): { id: string; name: string; scope: Scope }[] {
  if (!partDef.repeat) {
    return [{ id: partDef.id, name: partDef.name, scope: {} }];
  }
  let count: number;
  try {
    count = Math.max(0, Math.floor(evalValue(partDef.repeat.count, scope)));
  } catch (e) {
    issues.push(makeIssue(e, partDef.id, "repeat.count"));
    return [];
  }
  const out: { id: string; name: string; scope: Scope }[] = [];
  const v = partDef.repeat.var;
  for (let i = 0; i < count; i++) {
    out.push({
      id: `${partDef.id}_${i}`,
      name: partDef.name.replace(/\$\{([^}]+)\}/g, (_, src: string) => {
        try { return String(evalValue(src, { ...scope, [v]: i })); }
        catch { return _; }
      }),
      scope: { [v]: i, i },
    });
  }
  return out;
}

function evaluatePart(
  partDef: PartDef,
  override: PartOverride,
  scope: Scope,
  partId: string,
  partName: string,
  issues: Issue[],
): {
  cabinetPart: CabinetPart;
  mesh: CabinetMeshChunk;
  drillCtx: import("./drillingTemplates").DrillContext;
  min: number[];
  max: number[];
} | null {
  // enabled gate
  if (partDef.enabled) {
    try {
      if (evalValue(partDef.enabled, scope) === 0) return null;
    } catch (e) {
      issues.push(makeIssue(e, partId, "enabled"));
      return null;
    }
  }

  // box
  let min: number[], max: number[];
  try {
    min = partDef.box.min.map((s) => evalValue(s, scope));
    max = partDef.box.max.map((s) => evalValue(s, scope));
  } catch (e) {
    issues.push(makeIssue(e, partId, "box"));
    return null;
  }
  // boxOverride (string|number|null per axis)
  if (override.boxOverride) {
    const apply = (arr: number[], over?: (string | number | null)[]) => {
      if (!over) return arr;
      return arr.map((v, i) => {
        const o = over[i];
        if (o === null || o === undefined) return v;
        try { return evalValue(o, scope); } catch { return v; }
      });
    };
    min = apply(min, override.boxOverride.min);
    max = apply(max, override.boxOverride.max);
  }

  // cut
  let cutW: number, cutH: number;
  try {
    cutW = evalValue(partDef.cut.w, scope);
    cutH = evalValue(partDef.cut.h, scope);
  } catch (e) {
    issues.push(makeIssue(e, partId, "cut"));
    return null;
  }

  // thickness — overridable
  let thickness: number;
  try {
    const src = override.thickness ?? partDef.thickness;
    thickness = evalValue(src, scope);
  } catch (e) {
    issues.push(makeIssue(e, partId, "thickness"));
    return null;
  }

  const materialSlot = override.materialSlot ?? partDef.materialSlot;
  const color = partDef.color ?? MATERIAL_COLOR[materialSlot] ?? COLORS.carcass;

  const mesh = boxChunk(partId, min as [number, number, number], max as [number, number, number], color);

  const cabinetPart: CabinetPart = {
    id: partId,
    name: partName,
    w: cutW,
    h: cutH,
    thickness,
    materialId: 0,                  // resolved by bom.ts from materialSlot+role
    edges: { t: null, b: null, l: null, r: null },
    grain: partDef.grain,
    qty: 1,
    role: partDef.role,
  };

  return {
    cabinetPart,
    mesh,
    drillCtx: { part: cabinetPart, scope, partDef },
    min, max,
  };
}

// Pull a small set of screws across the joinery face. Two screws by
// default, spaced 1″ in from each end of the shared edge; matches the
// previous validator's "joint_missing_screws" warning (which checks for
// any screw drilling on the mating sides).
function makeJointScrews(
  me: { min: number[]; max: number[]; partDef: PartDef; cutW: number; cutH: number },
  mate: { min: number[]; max: number[]; partDef: PartDef },
  face: string,
  scope: Scope,
): DrillEntry[] {
  // Joinery emits onto the carcass-internal face. For X-mating (left/
  // right joined to bottom or back) the drillings are on the *side
  // panel's* inside face — but the legacy code drilled the side, not
  // the bottom/back. Here we only emit if the receiving part is a
  // side panel.
  const sideRoles = new Set(["side_left", "side_right"]);
  void face;
  if (!sideRoles.has(me.partDef.role)) return [];

  const T = (() => { try { return evalValue("T", scope); } catch { return 0.75; } })();
  // The shared edge between this side panel and `mate` runs along the
  // axis where the boxes overlap. For mate=bottom: overlap is in Z (the
  // bottom's z extent). For mate=back: overlap is in Y.
  // We pick the larger overlap axis and place 2 screws along it.
  const axes: Array<{ axis: 0 | 1 | 2; lo: number; hi: number }> = [];
  for (const ax of [0, 1, 2] as const) {
    const lo = Math.max(me.min[ax]!, mate.min[ax]!);
    const hi = Math.min(me.max[ax]!, mate.max[ax]!);
    if (hi > lo + 1e-6) axes.push({ axis: ax, lo, hi });
  }
  if (axes.length < 2) return [];
  // Drop the "thin" axis (the panel's thickness direction).
  axes.sort((a, b) => (b.hi - b.lo) - (a.hi - a.lo));
  const along = axes[0]!;  // longest shared axis
  const cross = axes[1]!;  // the other significant axis
  const drillsAlong = 2;
  const margin = 1;
  const span = along.hi - along.lo - 2 * margin;
  if (span <= 0) return [];
  // Map (along, cross) → part-local (x, y) on the "inside" face. For
  // side panels, the inside-face X is depth (cabinet Z), Y is height
  // (cabinet Y). So:
  //   axis 2 (Z) → part X
  //   axis 1 (Y) → part Y
  const mapPart = (worldAxis: 0|1|2, worldVal: number): [number, number] | null => {
    if (worldAxis === 2) return [worldVal - me.min[2]!, NaN];
    if (worldAxis === 1) return [NaN, worldVal - me.min[1]!];
    return null;
  };
  const out: DrillEntry[] = [];
  const yCross = (cross.lo + cross.hi) / 2;
  for (let i = 0; i < drillsAlong; i++) {
    const t = i / (drillsAlong - 1);
    const worldAlong = along.lo + margin + span * t;
    const ap = mapPart(along.axis, worldAlong);
    const cp = mapPart(cross.axis, yCross);
    if (!ap || !cp) continue;
    out.push({
      x: Number.isNaN(ap[0]) ? cp[0] : ap[0],
      y: Number.isNaN(ap[1]) ? cp[1] : ap[1],
      diameter: 5 / 25.4,
      depth: T,
      face: "inside",
      kind: "screw",
    });
  }
  return out;
}

function makeIssue(e: unknown, partId: string | undefined, field: string): Issue {
  const msg = e instanceof ExprError ? e.message
            : (e instanceof Error ? e.message : String(e));
  return {
    severity: "error",
    code: "cabinet_classid_unknown",  // reusing closest existing code
    message: `Evaluation failed in ${field}: ${msg}`,
    partId,
  };
}

// ── Adapter helpers for the legacy `CabinetClass` interface ─────
// These let us back the old `getCabinetClass(id).{mesh,parts,drillPattern}`
// callsites with the DSL evaluator without changing consumers.

export function legacyMesh(
  klass: ParametricCabinetClass,
  dims: { w: number; h: number; d: number },
): CabinetMeshChunk[] {
  return evaluateClass(
    { width: dims.w, height: dims.h, depth: dims.d, classParams: {}, partOverrides: {} },
    klass,
  ).meshes;
}

export function legacyParts(
  klass: ParametricCabinetClass,
  dims: { w: number; h: number; d: number },
): CabinetPart[] {
  return evaluateClass(
    { width: dims.w, height: dims.h, depth: dims.d, classParams: {}, partOverrides: {} },
    klass,
  ).parts;
}

export function legacyDrillPattern(
  klass: ParametricCabinetClass,
  dims: { w: number; h: number; d: number },
): DrillPattern {
  return evaluateClass(
    { width: dims.w, height: dims.h, depth: dims.d, classParams: {}, partOverrides: {} },
    klass,
  ).drillings;
}

/** Re-export the parser entry point so callers can validate user-
 *  authored expressions without pulling the evaluator. */
export { parse, asAst, evalAst };
